'use client'

import Button, { buttonClassNames } from '@components/primitives/Button'
import { IconSuffix } from '@components/primitives/Icon/IconSuffix'
import { EllipsesPulse } from '@components/primitives/LoadingWrapper/EllipsesPulse'
import { classMerge } from '@components/utilities/classMerge'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import debounce from 'lodash/debounce'

export default function TrialApplySubmitButton({
  answerValid = () => true,
  canSkip = false,
  disabled = false,
  isLoading = false,
  isStartOfQuestionnaire = true,
  onClick = () => {},
  questionUnanswered = () => true,
  slug,
}: {
  answerValid?: () => boolean
  canSkip?: boolean
  disabled?: boolean
  isLoading?: boolean
  isStartOfQuestionnaire?: boolean
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  questionUnanswered?: () => boolean
  slug?: string
}) {
  let buttonText = 'Next'
  let buttonStyles = buttonClassNames.legacy.fill.black
  let buttonDisabled = false

  if (isStartOfQuestionnaire) {
    buttonText = 'Start'
    buttonStyles = buttonClassNames.legacy.fill.primary
  } else {
    // if the answer is valid, we can move next
    if (answerValid() && !questionUnanswered()) {
      buttonText = 'Next'
      buttonStyles = buttonClassNames.legacy.fill.black
    } else if (canSkip) {
      buttonText = 'Skip'
      buttonStyles = buttonClassNames.legacy.fill.black
    } else if (disabled) {
      buttonText = 'Next'
      buttonStyles = buttonClassNames.legacy.accent.neutral
      buttonDisabled = true
    } else {
      // The answer is invalid, and we can't skip
      buttonText = 'Next'
      buttonStyles = buttonClassNames.legacy.accent.neutral
      buttonDisabled = true
    }
  }

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isStartOfQuestionnaire) {
      trackEvent(TrackingEvents.TRIAL_APPLICATION_START_BUTTON_CLICKED, {
        val: slug,
      })
    }
    onClick(event)
  }

  const debouncedHandleOnClick = debounce(handleOnClick, 250, {
    leading: true,
    trailing: false,
  })

  return (
    <Button
      className={classMerge(
        buttonStyles,
        'pointer-events-auto w-full md:w-1/4',
        isStartOfQuestionnaire ? buttonClassNames.primary.gradient : '',
        'rounded-full',
      )}
      disabled={buttonDisabled || isLoading}
      onClick={debouncedHandleOnClick}
      value={
        isLoading ? (
          <EllipsesPulse ellipsesColor='bg-white' />
        ) : (
          <IconSuffix icon={faArrowRight}>
            <span className='mr-2'>{buttonText}</span>
          </IconSuffix>
        )
      }
    />
  )
}
